import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { isApiError } from '../api/client';
import { Icon } from './Icon';
import s from './ui.module.css';

export function describeError(error: unknown): { title: string; message: string; requestId: string | null } {
  if (isApiError(error)) {
    const title =
      error.code === 'network' ? '無法連接 API'
      : error.code === 'timeout' ? '請求逾時'
      : error.status === 401 ? '登入已過期'
      : error.status === 403 ? '沒有權限'
      : error.status === 409 ? '狀態衝突'
      : error.status === 400 ? '請求無效'
      : error.status === 404 ? '找不到'
      : error.status === 429 ? '操作太頻密'
      : '請求失敗';
    const message = error.status === 403 && error.message === 'insufficient role' ? '你的後台角色不能執行這個操作。' : error.message;
    return { title: `${title}${error.status ? ` (${error.status} ${error.code})` : ''}`, message, requestId: error.requestId };
  }
  return { title: '未預期的錯誤', message: error instanceof Error ? error.message : String(error), requestId: null };
}

export function ErrorNote({ error, action }: { error: unknown; action?: ReactNode }) {
  const d = describeError(error);
  return (
    <div className={s.error} role="alert">
      <strong>{d.title}</strong>
      <span>{d.message}</span>
      {d.requestId && <span className="small faint">Request ID：<code>{d.requestId}</code></span>}
      {action && <div style={{ marginTop: 6 }}>{action}</div>}
    </div>
  );
}

export function SuccessNote({ children }: { children: ReactNode }) {
  return (
    <div className={s.success} role="status">
      {children}
    </div>
  );
}

export function Loading({ label = '載入中…' }: { label?: string }) {
  return (
    <div className={s.loading} role="status">
      <span className={s.spinner} aria-hidden />
      {label}
    </div>
  );
}

/** An empty list: what it means, and (as `hint`) what to do next. */
export function Empty({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <div className={s.empty}>
      <Icon name="inbox" className={s.emptyIcon} size={44} strokeWidth={1.4} />
      <span className={s.emptyTitle}>{children}</span>
      {hint && <span className="small">{hint}</span>}
    </div>
  );
}

/**
 * Renders loading / error / data for one query so pages only describe the happy path.
 * A failed background refetch keeps showing the last good data with the error above it.
 */
export function QueryView<T>({ query, children, loading, idle = null }: { query: Pick<UseQueryResult<T>, 'isPending' | 'fetchStatus' | 'data' | 'error' | 'isError'>; children: (data: T) => ReactNode; loading?: string; idle?: ReactNode }) {
  if (query.isPending) return query.fetchStatus === 'idle' ? <>{idle}</> : <Loading label={loading} />;
  if (query.data === undefined) return <ErrorNote error={query.error} />;
  return (
    <>
      {query.isError && <ErrorNote error={query.error} />}
      {children(query.data)}
    </>
  );
}
